import type { ProjectPath, SavedFilter, Sort } from "../api/generated";
import { formatProjectPath } from "./commands";

export type BuiltinShortcutId = "inbox" | "urgent" | "next" | "done";

export type ShortcutKind = "builtin" | "saved" | "tag" | "project";

export type Grouping = "none" | "due-day";

/** The whole state of the task list: which tasks, in what order. Everything else is derived. */
export interface ListQuery {
  filter: string;
  sort: Sort;
}

/**
 * A sidebar entry. It is a memoryless preset: choosing it replaces the current list query with
 * `query`, and it counts as current only while the list query still equals `query`.
 */
export interface Shortcut {
  kind: ShortcutKind;
  /** Unique among shortcuts of the same kind; only for React keys. */
  key: string;
  title: string;
  query: ListQuery;
  /** Attributes the quick-add box applies to a new task when the user types none. */
  quickAddDefaults: { project?: string; dueText?: string; tag?: string };
}

export const BUILTIN_SHORTCUTS: readonly { id: BuiltinShortcutId; title: string }[] = [
  { id: "inbox", title: "Inbox" },
  { id: "urgent", title: "Urgent" },
  { id: "next", title: "Next" },
  { id: "done", title: "Completed" },
];

/**
 * Built-in filters are written out in full, never as the `ready` / `next` words or an empty
 * filter, so the filter box always shows what a shortcut really selects. `ready` and `next` are
 * shorthand for `status:pending`; an empty filter means every open task.
 */
export const URGENT_FILTER = "status:pending";
export const OPEN_FILTER = "status:pending or status:waiting or status:blocked";

export function quoteFilterValue(value: string): string {
  const escaped = value.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
  return /[\s"()]/.test(value) ? `"${escaped}"` : value;
}

export function builtinShortcut(id: BuiltinShortcutId): Shortcut {
  switch (id) {
    case "inbox":
      return shortcut("builtin", "inbox", "Inbox", "project:none", "Urgency", {});
    case "urgent":
      return shortcut("builtin", "urgent", "Urgent", URGENT_FILTER, "Urgency", {});
    case "next":
      return shortcut("builtin", "next", "Next", OPEN_FILTER, "Due", {});
    case "done":
      return shortcut("builtin", "done", "Completed", "status:done", "Due", {});
  }
}

export function projectShortcut(path: ProjectPath): Shortcut {
  const name = formatProjectPath(path);
  return shortcut("project", name, path.at(-1) ?? "Projects", `project:${quoteFilterValue(name)}`, "Urgency", {
    project: name,
  });
}

export function tagShortcut(tag: string): Shortcut {
  return shortcut("tag", tag, tag, `tag:${quoteFilterValue(tag)}`, "Urgency", { tag });
}

export function savedFilterShortcut(saved: SavedFilter): Shortcut {
  return shortcut("saved", saved.name, saved.name, saved.expression, saved.sort, {});
}

function shortcut(
  kind: ShortcutKind,
  key: string,
  title: string,
  filter: string,
  sort: Sort,
  quickAddDefaults: Shortcut["quickAddDefaults"],
): Shortcut {
  return { kind, key, title, query: { filter, sort }, quickAddDefaults };
}

/** Where the app lands: the Next shortcut. */
export const DEFAULT_QUERY: ListQuery = builtinShortcut("next").query;

export function sameQuery(left: ListQuery, right: ListQuery): boolean {
  return left.sort === right.sort && left.filter.trim() === right.filter.trim();
}

/** The shortcuts the list query currently equals, in the order given; usually none or one. */
export function currentShortcuts(query: ListQuery, shortcuts: readonly Shortcut[]): Shortcut[] {
  return shortcuts.filter((candidate) => sameQuery(query, candidate.query));
}

const SORT_WORDS: Record<Sort, string> = {
  Urgency: "urgency",
  Due: "due date",
  Priority: "priority",
  Added: "date added",
  Project: "project",
  Title: "title",
};

export const SORT_OPTIONS: readonly Sort[] = ["Urgency", "Due", "Priority", "Added", "Project", "Title"];

export function sortWords(sort: Sort): string {
  return SORT_WORDS[sort];
}

/** Grouping follows from the sort alone: sorting by due date groups by day, anything else is flat. */
export function groupingForSort(sort: Sort): Grouping {
  return sort === "Due" ? "due-day" : "none";
}

export function groupingWords(grouping: Grouping): string | undefined {
  return grouping === "due-day" ? "day" : undefined;
}
